import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthCredentials, SiteCredentials } from '@models/interfaces/Auth/SiteCredentials.interface';
import { environment } from '../../environments/environment';
import { Observable, switchMap, tap } from 'rxjs';
import { ApiResponse } from '@models/interfaces/core/ApiResponse.interface';


@Injectable({
  providedIn: 'root'
})
export class CredentialsService {

  // SERVICES
  private readonly http: HttpClient = inject(HttpClient);

  // STATE
  private readonly credentialManager: Map<number, SiteCredentials[]> = new Map<number, SiteCredentials[]>();

  // API
  private readonly endpoint: string = '/api/accounts/manage/';
  private readonly api:      string = environment.apiUrl + this.endpoint;


  // #region CREDENTIALS METHODS
  public setCredentials(previewId: number, credentials: SiteCredentials): void {

    const credentialsData: SiteCredentials[] = this.credentialManager.get(previewId) || [];

    credentialsData.push(credentials);

    this.credentialManager.set(previewId, credentialsData);

  }

  public getCredentials(previewId: number): SiteCredentials[] | null {

    return this.credentialManager.get(previewId) || null;

  }

  public clearCredentials(): void {

    this.credentialManager.clear();

  }

  public loginPreview(data: {accountName: string, password: string, previewId: number}): Observable<AuthCredentials> {
    
    return this.loginCredentials(data).pipe(
      tap((res) => res ? localStorage.setItem(res.previewId.toString(), res.auth.token) : null)
    );
  
  }

  public deleteUserCredentials(id: number): Observable<ApiResponse<unknown>> {

    return this.deleteCredentials(id);

  }

  // #endregion

  // #region API METHODS
  public getAllCredentials(): Observable<ApiResponse<SiteCredentials[]>> {
    
    this.clearCredentials();

    return this.http.get<ApiResponse<SiteCredentials[]>>(`${this.api}`).pipe(
      tap((res: ApiResponse<SiteCredentials[]>) => res.data.forEach(credential => this.setCredentials(credential.previewId, credential))),
      tap((): void => console.log(this.credentialManager))
    );

  }

  public getCredentialsByPreview(previewId: number): Observable<ApiResponse<SiteCredentials[]>> {

    return this.http.get<ApiResponse<SiteCredentials[]>>(`${this.api}${previewId}/`).pipe(
      tap((res: ApiResponse<SiteCredentials[]>) => res.data.forEach(credential => this.setCredentials(previewId, credential)))
    );

  }

  public updateCredentials(id: number, body: {password: string}): Observable<ApiResponse<SiteCredentials[]>> {

    return this.http.patch<ApiResponse<SiteCredentials>>(`${this.api}${id}/`, body).pipe(
      switchMap((): Observable<ApiResponse<SiteCredentials[]>> => this.getAllCredentials())
    );

  }

  public createCredentials(data: Partial<SiteCredentials>): Observable<ApiResponse<SiteCredentials[]>> {

    return this.http.post<ApiResponse<SiteCredentials>>(`${this.api}`, data).pipe(
      switchMap((): Observable<ApiResponse<SiteCredentials[]>> => this.getAllCredentials())
    );

  }

  public loginCredentials(body: {accountName: string, password: string, previewId: number }): Observable<AuthCredentials> {
    
    const api: string = environment.apiUrl + '/api/previews/auth/login/';
    
    return this.http.post<AuthCredentials>(api, body);
  }

  private deleteCredentials(id: number): Observable<ApiResponse<SiteCredentials[]>> {

    return this.http.delete<ApiResponse<unknown>>(`${this.api}${id}/`).pipe(
      switchMap((): Observable<ApiResponse<SiteCredentials[]>> => this.getAllCredentials())
    );

  }

  // #endregion

}
